import axiosInstance from '../../../services/axios/axios';
import { then } from '../../../services/axios/_utility';
import { WrapImsPacket } from '../../../store/utility';
/**
 * Asynchronously retrieves the client's IPv6 address.
 *
 * @function
 * @returns {Promise} nullable IPv6 address of user.
 */
export const getIPAddress = async () => {
	return await axiosInstance
		.get('api/Applications/User/GetIPAddress')
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(() => null);
};

/**
 * Asynchronously generates a new application request tracking number.
 *
 * @function
 * @param {boolean} isMultiUser flag toggling multi-user tracking number variant.
 * @returns {Promise} Resolved promise providing generated tracking number.
 */
export const getTrackingNumber = async (isMultiUser = false) => {
	return await axiosInstance
		.get('api/Applications/Requests/GetTrackingNumber', {
			params: {
				isMultiUser: isMultiUser,
			},
		})
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously retrieves user type of user of interest.
 *
 * @function
 * @param {string} id unique identifier of user of interest.
 * @returns {Promise} Resolved promise providing user type of user of interest.
 */
export const getUserType = async id => {
	return await axiosInstance
		.get('api/Eso/NonEmployeeType', {
			params: {
				ID: id,
			},
		})
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously retrieves requestee from new non-employee request of interest.
 *
 * @function
 * @param {string} trackingNumber unique identifier of new non-employee request of interest.
 * @returns {Object} new, non-employee requestee.
 */
export const getNewRequesteeByTrackingNumber = async trackingNumber => {
	return await axiosInstance
		.get('api/RequestFlow/GetNewRequesteeByTrackingNumber', {
			params: {
				trackingNumber: trackingNumber,
			},
		})
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously retrieves requestees from bulk new non-employee computer access request of interest.
 *
 * @function
 * @param {string} trackingNumber unique identifier of bulk new non-employee computer access request of interest.
 * @returns {Object} new, non-employee requestees.
 */
export const getNewRequesteesByTrackingNumber = async trackingNumber => {
	return await axiosInstance
		.get('api/RequestFlow/GetNewRequesteesByTrackingNumber', {
			params: {
				trackingNumber: trackingNumber,
			},
		})
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously retrieves data table requestee row from ID of logged-in user.
 *
 * @function
 * @param {number} id unique identifier of user.
 * @returns {Promise} Resolved promise representing a data table row of the current requestee.
 */
export const getRequesteeRowByID = async id => {
	return await axiosInstance
		.get('api/RequestFlow/GetRequesteeRowByID', {
			params: {
				id: id,
			},
		})
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes and completes requests not advancing for computer access.
 *
 * @function
 * @param {string} trackingNumber unique identifier of current request.
 * @returns {boolean} success indication.
 */
export const postNoComputerAccess = async trackingNumber => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostNoComputerAccess',
			WrapImsPacket(trackingNumber)
		)
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes acknowledgment of staff access agreement.
 *
 * @function
 * @param {string} trackingNumber tracking number of associated request.
 * @param {string} ID unique identifer of staff member acknowledging agreement.
 * @param {string} ipAddress IPv6 address of client.
 * @returns {boolean} success indication.
 */
export const postStaffAccessAgreement = async (
	trackingNumber,
	ID,
	ipAddress
) => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostStaffAccessAgreement',
			WrapImsPacket({
				TrackingNumber: trackingNumber,
				ID: ID,
				IPAddress: ipAddress,
			})
		)
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes acknowledgment of staff access agreement for service account request.
 *
 * @function
 * @param {string} trackingNumber tracking number of associated request.
 * @param {string} ID unique identifer of staff member acknowledging agreement.
 * @param {string} ipAddress IPv6 address of client.
 * @returns {boolean} success indication.
 */
export const postStaffAccessAgreementServiceAccount = async (
	trackingNumber,
	ID,
	ipAddress
) => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostStaffAccessAgreementServiceAccount',
			WrapImsPacket({
				TrackingNumber: trackingNumber,
				ID: ID,
				IPAddress: ipAddress,
			})
		)
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes acknowledgment of UPMC Surveyor Guidelines provisioning.
 *
 * @function
 * @param {string} trackingNumber unique identifier of current request.
 * @returns {boolean} success indication.
 */
export const postSurveyorGuidelines = async trackingNumber => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostSurveyorGuidelines',
			WrapImsPacket(trackingNumber)
		)
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes system access agreement.
 *
 * @function
 * @param {object} form aggregated input field value(s).
 * @param {string} requesteeIDs unique identifier of user(s) of interest.
 * @param {string} requestorID unique identifier of user performing user type change.
 * @param {string} trackingNumber tracking number of associated request.
 * @param {Function} dispatch forwarded Redux store's method for dispatching actions.
 * @returns {Promise} Resolved promise providing agreement file name.
 */
export const postSystemAccessAgreement = async (
	form,
	requesteeIDs,
	requestorID,
	trackingNumber,
	dispatch = null
) => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostSystemAccessAgreement',
			WrapImsPacket({
				Form: form,
				RequesteeIDs: requesteeIDs,
				RequestorID: requestorID,
				TrackingNumber: trackingNumber,
			})
		)
		.then(response => {
			return then(response, payload => payload, dispatch);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};

/**
 * Asynchronously processes user type change.
 *
 * @function
 * @param {object} form aggregated input field value(s).
 * @param {string} requesteeID unique identifier of user of interest.
 * @param {string} requestorID unique identifier of user performing user type change.
 * @param {string} trackingNumber tracking number of associated request.
 * @returns {Promise} Resolved promise indicating success.
 */
export const postUserType = async (
	form,
	requesteeID,
	requestorID,
	trackingNumber
) => {
	return await axiosInstance
		.post(
			'api/RequestFlow/PostUserType',
			WrapImsPacket({
				Form: form,
				RequesteeID: requesteeID,
				RequestorID: requestorID,
				TrackingNumber: trackingNumber,
			})
		)
		.then(response => {
			return then(response, payload => payload);
		})
		.catch(error => {
			throw error.cause ? error.cause : error;
		});
};
